import { Box, FormControl, FormControlLabel, IconButton, InputLabel, MenuItem, Paper, Select, Switch, TextField, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import StarIcon from '@mui/icons-material/Star';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import LooksOneIcon from '@mui/icons-material/LooksOne';
import PublishIcon from '@mui/icons-material/Publish';
import WarningIcon from '@mui/icons-material/Warning';
import Grid from "@mui/material/Grid2";
import moment from 'moment';
import { useSelector } from 'src/stateManagement/redux';
import { selectMainState } from 'src/stateManagement/redux';
import { IUpdateDiagnosticForActivity } from 'src/shared/util/updateDataServer';
import { FileAttachment } from '../files/fileattachment';

const status_candidato_listy = [
    { id: "Reevaluación", name: "Reevaluación" },
    { id: "Oportunidad de Incremento", name: "Oportunidad de Incremento" },
    { id: "Mantener Condiciones", name: "Mantener Condiciones" }
];

const taskStates = [
    { id: "pending", name: "Por Analizar" },
    { id: "analyzing", name: "Evaluar Luego" },
    { id: "finished", name: "Analizada" }
];

export interface DiagnosticoRightPanelProps {
    activeDiagnostic: IUpdateDiagnosticForActivity | undefined;
    onDiagnosticChange: (value: any, label: string) => void;
    condicionFondoList: { id: string; name: string; enabledOn?: string[] }[];
    lastProductionTest?: any;
    lastWaterTest?: any;
    averageWaterTestsLast5?: { average: number; standardDeviation: number; startDate: string; endDate: string };
    originFieldActivityId?: string;
    /** Cuando true, el panel está al 50% (balanced). Fuentes más pequeñas. */
    isCompact?: boolean;
    addRemoveOptimizationActivity?: (activity: any, add: boolean) => void;
    handleConvertToMain?: (activity: any) => void;
    onOpenQuickAddRequirement?: () => void;
    onOpenQuickAddOptimization?: () => void;
    onGenerateDiagnostic?: () => void;
}

const fontScale = (isCompact: boolean) => ({
    label: isCompact ? 12 : 15,
    cardTitle: isCompact ? 13 : 18,
    cardValue: isCompact ? 20 : 23,
    cardValueSmall: isCompact ? 11 : 14,
    cardTdsLabel: isCompact ? 14 : 17,
    cardCaption: isCompact ? '0.75rem' : '0.85rem',
    body: isCompact ? 12 : 15,
    runLife: isCompact ? 13 : 16,
    paperLabel: isCompact ? 13 : 16,
});

const filterTypeOfActivity = (activities: any[], typeOfActivity: 'Requirement' | 'Optimization', configModules: any): any[] => {
    const requiredActivities = configModules?.typesActivitiesForRequirements?.map((a: any) => a.label) || [];
    const optimizationActivities = configModules?.typesActivitiesForOptimization?.map((a: any) => a.label) || [];
    if (typeOfActivity === 'Requirement') {
        return activities.filter((activity) => requiredActivities.includes(activity.name));
    }
    if (typeOfActivity === 'Optimization') {
        return activities.filter((activity) => optimizationActivities.includes(activity.name));
    }
    return [];
};

const emptyMessageStyle = { display: 'flex', justifyContent: 'center', padding: '5px 0px', alignItems: 'center', fontSize: 14, color: 'rgba(248, 6, 6, 0.4)' };
const addButtonSx = {
    backgroundColor: 'custom.blue2',
    color: '#fff',
    width: '25px',
    height: '25px',
    '&:hover': { backgroundColor: 'custom.blue1' },
};

export const DiagnosticoRightPanel = ({
    activeDiagnostic,
    onDiagnosticChange,
    condicionFondoList,
    lastProductionTest,
    lastWaterTest,
    averageWaterTestsLast5,
    originFieldActivityId,
    isCompact = false,
    addRemoveOptimizationActivity,
    handleConvertToMain,
    onOpenQuickAddRequirement,
    onOpenQuickAddOptimization,
    onGenerateDiagnostic,
}: DiagnosticoRightPanelProps) => {
    const fs = fontScale(isCompact);
    const { configModules } = useSelector(selectMainState);

    const requirementsList = filterTypeOfActivity(activeDiagnostic?.optimizationActivitiesSelected || [], 'Requirement', configModules);
    const optionsList = filterTypeOfActivity(activeDiagnostic?.optimizationActivitiesSelected || [], 'Optimization', configModules);
    const hasRequirementHandlers = !!addRemoveOptimizationActivity && !!handleConvertToMain;
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: isCompact ? 1.5 : 2, minHeight: 'min-content' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: activeDiagnostic?.analyzedBy ? 'space-between' : 'flex-end', width: '100%' }}>
                {activeDiagnostic?.analyzedBy && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: fs.body }}>
                        Analizado por: {activeDiagnostic.analyzedBy} el {moment(activeDiagnostic.analyzedDate || '').format('DD/MM/YYYY')}
                    </Typography>
                )}
                <Tooltip title="Generar diagnóstico">
                    <IconButton size="small" onClick={onGenerateDiagnostic} disabled={!onGenerateDiagnostic}>
                        <AutoAwesomeIcon color="primary" />
                    </IconButton>
                </Tooltip>
            </Box>

            <Paper elevation={2} sx={{ p: isCompact ? 1.5 : 2 }}>
                <Typography className="label-input" sx={{ fontSize: fs.paperLabel, fontWeight: 700, color: 'custom.blue2', mb: 1 }}>
                    DIAGNÓSTICO
                </Typography>
                <TextField
                    size="small"
                    value={activeDiagnostic?.diagnostic || ''}
                    onChange={(e) => onDiagnosticChange(e.target.value, 'diagnostic')}
                    fullWidth
                    error={!activeDiagnostic?.diagnostic || activeDiagnostic.diagnostic.trim() === ''}
                    multiline
                    rows={isCompact ? 2 : 3}
                    sx={{ '& .MuiOutlinedInput-root fieldset': { border: 'none' } }}
                />
            </Paper>

            <Paper elevation={2} sx={{ p: isCompact ? 1.5 : 2 }}>
                <Typography className="label-input" sx={{ fontSize: fs.paperLabel, fontWeight: 700, color: 'custom.blue2', mb: 1 }}>
                    RECOMENDACIÓN
                </Typography>
                <TextField
                    size="small"
                    value={activeDiagnostic?.recommendation || ''}
                    onChange={(e) => onDiagnosticChange(e.target.value, 'recommendation')}
                    fullWidth
                    error={!activeDiagnostic?.recommendation || activeDiagnostic.recommendation.trim() === ''}
                    multiline
                    rows={isCompact ? 2 : 3}
                    sx={{ '& .MuiOutlinedInput-root fieldset': { border: 'none' } }}
                />
            </Paper>

            <Grid container spacing={2} sx={{ mb: 1, display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'stretch', width: '100%' }}>
                <Grid size={4}>
                    <Paper elevation={3} sx={{ height: '100%' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', p: isCompact ? 0.75 : 1, justifyContent: 'center', alignItems: 'center', gap: 0.25 }}>
                            <Typography sx={{ fontSize: fs.cardTitle, color: 'custom.blue2', fontWeight: 'bold', textAlign: 'center' }}>% AyS</Typography>
                            <Grid container spacing={0.5} size={12}>
                                <Grid size={8}>
                                    <Typography sx={{ lineHeight: 1.2, fontWeight: 600, color: 'custom.blue2', fontSize: fs.cardTitle }}>Último: </Typography>
                                    <Typography variant="caption" sx={{ fontSize: fs.cardCaption, lineHeight: 1 }}>{lastWaterTest?.samplingDate ? moment(lastWaterTest.samplingDate).format('DD-MMM-YY') : ''}</Typography>
                                </Grid>
                                <Grid size={4}>
                                    <Typography sx={{ lineHeight: 1.2, color: 'custom.icons', fontWeight: 'bold', fontSize: fs.cardTitle }}>{lastWaterTest?.totalWaterPercentage ? Math.round(lastWaterTest.totalWaterPercentage) : 0}%</Typography>
                                </Grid>
                            </Grid>
                            <Grid container spacing={0.5} size={12}>
                                <Grid size={8}>
                                    <Typography sx={{ lineHeight: 1.2, fontWeight: 600, color: 'custom.blue2', fontSize: fs.cardTitle }}>Promedio: </Typography>
                                    <Typography variant="caption" sx={{ fontSize: fs.cardCaption, lineHeight: 1 }}>{averageWaterTestsLast5?.startDate ? moment(averageWaterTestsLast5.startDate).format('DD-MMM') : ''} al {averageWaterTestsLast5?.endDate ? moment(averageWaterTestsLast5.endDate).format('DD-MMM') : ''}</Typography>
                                </Grid>
                                <Grid size={4}>
                                    <Typography sx={{ lineHeight: 1.2, color: 'custom.icons', fontWeight: 'bold', fontSize: fs.cardTitle }}>{averageWaterTestsLast5 ? Math.round(averageWaterTestsLast5.average) : 0}%</Typography>
                                </Grid>
                            </Grid>
                        </Box>
                    </Paper>
                </Grid>
                <Grid size={4}>
                    <Paper elevation={3} sx={{ height: '100%' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', p: isCompact ? 0.75 : 1, justifyContent: 'center', alignItems: 'center', gap: 0.25 }}>
                            <Typography sx={{ fontSize: fs.cardTitle, color: 'custom.blue2', fontWeight: 'bold', textAlign: 'center' }}>ÚLTIMA PRUEBA</Typography>
                            <Typography sx={{ fontSize: fs.cardValue, color: 'custom.icons', fontWeight: 'bold', textAlign: 'center' }}>
                                {lastProductionTest?.netProduction ? Math.round(lastProductionTest.netProduction) : 0} / {lastProductionTest?.grossProduction ? Math.round(lastProductionTest.grossProduction) : 0}
                            </Typography>
                            <Typography sx={{ fontSize: fs.cardValueSmall, fontWeight: 'bold', textAlign: 'right', width: '100%' }}>
                                {lastProductionTest?.testDate ? moment(lastProductionTest.testDate).format('DD-MMM-YY') : ''} ({lastProductionTest?.testCondition || ''})
                            </Typography>
                        </Box>
                    </Paper>
                </Grid>
                <Grid size={4}>
                    <Paper elevation={3} sx={{ height: '100%' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', p: isCompact ? 0.75 : 1, justifyContent: 'center', alignItems: 'center' }}>
                            <Typography sx={{ fontSize: fs.cardTitle, color: 'custom.blue2', fontWeight: 'bold', textAlign: 'center' }}>POZO REPETITIVO TDS</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around' }}>
                                <Typography sx={{ fontSize: fs.cardTdsLabel, color: 'custom.blue2', fontWeight: 'bold', textAlign: 'right', mr: 2 }}>No</Typography>
                                <FormControlLabel
                                    label=""
                                    control={
                                        <Switch
                                            checked={(activeDiagnostic as any)?.isWellWithMultiplesTDSEntries || false}
                                            onChange={(e) => onDiagnosticChange(e.target.checked, 'isWellWithMultiplesTDSEntries')}
                                        />
                                    }
                                />
                                <Typography sx={{ fontSize: fs.cardTdsLabel, color: 'custom.blue2', fontWeight: 'bold', textAlign: 'center' }}>Si</Typography>
                            </Box>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography sx={{ fontSize: fs.runLife, color: 'custom.blue2', fontWeight: 'bold' }}>RUN LIFE (Carta Activa)</Typography>
                <Typography variant="body2" sx={{ color: 'custom.icons', fontWeight: 600, fontSize: fs.runLife }}>
                    {activeDiagnostic?.runLife ? Math.round(activeDiagnostic.runLife) : 0} días
                </Typography>
                {activeDiagnostic?.lastSubSurfaceJobFinishedDate && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', ml: 'auto' }}>
                        {moment(activeDiagnostic.lastSubSurfaceJobFinishedDate).format('DD-MMM-YY')}
                    </Typography>
                )}
            </Box>

            <Grid container spacing={2}>
                <Grid size={6}>
                    <FormControl size="small" fullWidth sx={{ mb: 2, '& .MuiOutlinedInput-root': { backgroundColor: '#f0f0f0', '& fieldset': { border: 'none' } } }}>
                        <InputLabel>ESTATUS CANDIDATO</InputLabel>
                        <Select
                            value={activeDiagnostic?.candidateSate || ''}
                            label="ESTATUS CANDIDATO"
                            onChange={(e) => onDiagnosticChange(e.target.value, 'candidateSate')}
                        >
                            {status_candidato_listy.map((s) => (
                                <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" fullWidth sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#f0f0f0', '& fieldset': { border: 'none' } } }}>
                        <InputLabel>CONDICIÓN DE FONDO</InputLabel>
                        <Select
                            value={activeDiagnostic?.bottomHoleCondition || ''}
                            label="CONDICIÓN DE FONDO"
                            onChange={(e) => onDiagnosticChange(e.target.value, 'bottomHoleCondition')}
                        >
                            {condicionFondoList.map((c) => (
                                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl size="small" fullWidth sx={{ mb: 2, '& .MuiOutlinedInput-root': { backgroundColor: '#f0f0f0', '& fieldset': { border: 'none' } } }}>
                        <InputLabel>NUEVO ESTADO TAREA</InputLabel>
                        <Select
                            value={activeDiagnostic?.newFieldActivityState || ''}
                            label="NUEVO ESTADO TAREA"
                            onChange={(e) => onDiagnosticChange(e.target.value, 'newFieldActivityState')}
                        >
                            {taskStates.map((t) => (
                                <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" fullWidth sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#f0f0f0', '& fieldset': { border: 'none' } } }}>
                        <InputLabel>SUB-CONDICIÓN</InputLabel>
                        <Select
                            value={activeDiagnostic?.subBottomHoleCondition || ''}
                            label="SUB-CONDICIÓN"
                            disabled
                            readOnly
                        >
                            <MenuItem value={activeDiagnostic?.subBottomHoleCondition || ''}>{activeDiagnostic?.subBottomHoleCondition || '-'}</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 1 }}>
                <Grid size={isCompact ? 12 : 6}>
                    <Paper elevation={3} sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography className="label-input" sx={{ fontSize: fs.paperLabel, fontWeight: 700, color: 'custom.blue2' }}>
                                REQUERIMIENTOS DE OPTIMIZACIÓN
                            </Typography>
                            {originFieldActivityId && onOpenQuickAddRequirement && (
                                <IconButton size="small" aria-label="Agregar requerimiento" onClick={onOpenQuickAddRequirement} sx={addButtonSx}>
                                    <AddIcon fontSize="small" />
                                </IconButton>
                            )}
                        </Box>
                        {requirementsList.map((opt, i) => (
                            <Box
                                key={i}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    backgroundColor: 'custom.grey1',
                                    borderRadius: '10px',
                                    padding: '5px 15px',
                                    alignItems: 'center',
                                    mt: 1,
                                }}
                            >
                                <Typography sx={{ color: 'custom.blue2', fontSize: 12, fontWeight: 'bold' }}>{opt?.name?.toUpperCase()}</Typography>
                                {hasRequirementHandlers && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Tooltip title="Convertir en principal">
                                            <IconButton onClick={() => handleConvertToMain?.(opt)}>
                                                {opt?.main ? <LooksOneIcon sx={{ color: 'custom.blue2' }} /> : <PublishIcon sx={{ color: 'custom.blue2' }} />}
                                            </IconButton>
                                        </Tooltip>
                                        {opt?.isWellWithMultiplesTDSEntries && (
                                            <Tooltip title="Pozo con multiples entradas TDS">
                                                <WarningIcon color="warning" fontSize="small" />
                                            </Tooltip>
                                        )}
                                        <IconButton size="small" color="error" onClick={() => addRemoveOptimizationActivity?.(opt, false)}>
                                            <DeleteForeverIcon fontSize="medium" />
                                        </IconButton>
                                    </Box>
                                )}
                            </Box>
                        ))}
                        {requirementsList.length === 0 && (
                            <Box sx={emptyMessageStyle}>
                                Debe Seleccionar al menos una opción de optimization o requerimientos de las disponibles
                            </Box>
                        )}
                    </Paper>
                </Grid>
                <Grid size={isCompact ? 12 : 6}>
                    <Paper elevation={3} sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                            <Typography className="label-input" sx={{ fontSize: fs.paperLabel, fontWeight: 700, color: 'custom.blue2' }}>
                                OPCIONES DE OPTIMIZACIÓN
                            </Typography>
                            {originFieldActivityId && onOpenQuickAddOptimization && (
                                <IconButton size="small" aria-label="Agregar opción" onClick={onOpenQuickAddOptimization} sx={addButtonSx}>
                                    <AddIcon fontSize="small" />
                                </IconButton>
                            )}
                        </Box>
                        {optionsList.map((opt, i) => (
                            <Box
                                key={i}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    backgroundColor: 'custom.grey1',
                                    borderRadius: '10px',
                                    padding: '5px 15px',
                                    alignItems: 'center',
                                    mt: 1,
                                }}
                            >
                                <Typography sx={{ color: 'custom.blue2', fontSize: 12, fontWeight: 'bold' }}>{opt?.name?.toUpperCase()}</Typography>
                                {hasRequirementHandlers && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Tooltip title="Convertir en principal">
                                            <IconButton onClick={() => handleConvertToMain?.(opt)}>
                                                {opt?.main ? <LooksOneIcon sx={{ color: 'custom.blue2' }} /> : <PublishIcon sx={{ color: 'custom.blue2' }} />}
                                            </IconButton>
                                        </Tooltip>
                                        {opt?.isWellWithMultiplesTDSEntries && (
                                            <Tooltip title="Pozo con multiples entradas TDS">
                                                <WarningIcon color="warning" fontSize="small" />
                                            </Tooltip>
                                        )}
                                        <IconButton size="small" color="error" onClick={() => addRemoveOptimizationActivity?.(opt, false)}>
                                            <DeleteForeverIcon fontSize="medium" />
                                        </IconButton>
                                    </Box>
                                )}
                            </Box>
                        ))}
                        {optionsList.length === 0 && (
                            <Box sx={emptyMessageStyle}>
                                Debe Seleccionar al menos una opción de optimization de las disponibles
                            </Box>
                        )}
                    </Paper>
                </Grid>
                <Grid size={isCompact ? 12 : 6}>
                    <Paper elevation={3} sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                            <Typography className="label-input" sx={{ fontSize: fs.paperLabel, fontWeight: 700, color: 'custom.blue2', alignSelf: 'flex-start' }}>
                                ADJUNTOS
                            </Typography>
                        </Box>
                        {originFieldActivityId ? (
                            <Box sx={{ maxHeight: 200, overflow: 'auto', mt: 1 }}>
                                <FileAttachment
                                    originActivityId={originFieldActivityId}
                                    activeDiagnostic={activeDiagnostic}
                                    addRemoveAttachment={undefined}
                                    compactEmptyState
                                />
                            </Box>
                        ) : (
                            <Box sx={emptyMessageStyle}>No tiene soporte adjunto</Box>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};
